import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { mediaList } from '@/mocks/movies';

const featuredIds = ['m03', 'm01', 'm04', 'm14', 'm28'];

const slides = featuredIds
  .map((id) => mediaList.find((item) => item.id === id))
  .filter((item): item is (typeof mediaList)[number] => Boolean(item));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const go = (delta: number) => {
    setIndex((prev) => (prev + delta + slides.length) % slides.length);
  };

  const active = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden rounded-xl bg-background-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="影视焦点轮播"
    >
      <div className="relative h-[420px] w-full md:h-[520px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${slideIndex === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <img
              src={slide.backdrop}
              alt={`${slide.title} 剧照`}
              title={`${slide.title} 影视焦点推荐`}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/90 via-background-950/60 to-background-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-transparent to-transparent" />
      </div>

      <div className="absolute inset-0 flex flex-col justify-end p-5 md:justify-center md:p-12">
        <div className="max-w-xl">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-500 px-2.5 py-0.5 text-[11px] font-medium text-background-50">焦点推荐</span>
            <span className="rounded-full border border-background-100/30 px-2.5 py-0.5 text-[11px] text-background-100/90">{active.type}</span>
            <span className="flex items-center gap-1 rounded-full bg-background-50/15 px-2.5 py-0.5 text-[11px] text-accent-300">
              <i className="ri-star-fill w-3 h-3 flex items-center justify-center text-[10px]" />
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
            <Link to={`/detail/${active.id}`} className="transition hover:text-accent-300">{active.title}</Link>
          </h1>
          <p className="mt-2 text-xs text-background-100/70 md:text-sm">
            {active.enTitle} · {active.year} · {active.region} · {active.runtime}
          </p>
          <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-background-100/80 md:line-clamp-3 md:text-sm">{active.summary}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600"
            >
              查看详情
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
            <Link
              to={`/category/${encodeURIComponent(active.type)}`}
              className="whitespace-nowrap rounded-md border border-background-100/30 px-4 py-2 text-sm text-background-100 transition hover:border-accent-400 hover:text-accent-300"
            >
              更多{active.type}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-100/25 bg-background-950/30 text-background-100 transition hover:bg-primary-500 hover:text-background-50 md:flex"
      >
        <i className="ri-arrow-left-s-line text-lg" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-100/25 bg-background-950/30 text-background-100 transition hover:bg-primary-500 hover:text-background-50 md:flex"
      >
        <i className="ri-arrow-right-s-line text-lg" />
      </button>

      <div className="absolute bottom-4 right-4 hidden items-center gap-2 md:flex">
        {slides.map((slide, slideIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(slideIndex)}
            aria-label={`切换到 ${slide.title}`}
            className={`h-1.5 rounded-full transition-all ${slideIndex === index ? 'w-7 bg-primary-400' : 'w-3 bg-background-100/40 hover:bg-background-100/70'}`}
          />
        ))}
      </div>
    </section>
  );
}